import { FC, useState } from "react";
import { Box, Flex, Text, VStack, Portal } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  FiHome,
  FiUser,
  FiMail,
  FiBriefcase,
  FiPower,
  FiHelpCircle,
  FiFolder,
} from "react-icons/fi";

interface StartMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export const StartMenu: FC<StartMenuProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [showShutdownDialog, setShowShutdownDialog] = useState(false);
  const [showAboutDialog, setShowAboutDialog] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [shutdownOption, setShutdownOption] = useState<"shutdown" | "restart">(
    "shutdown",
  );

  if (!isOpen && !showShutdownDialog && !showAboutDialog) return null;

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleShutdown = () => {
    setShowShutdownDialog(true);
    onClose();
  };

  const confirmShutdown = () => {
    setShowShutdownDialog(false);

    if (shutdownOption === "restart") {
      window.location.reload();
      return;
    }

    // Create shutdown screen as a new element
    const shutdownScreen = document.createElement("div");
    shutdownScreen.id = "shutdown-screen";
    shutdownScreen.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: black;
      color: #FF6600;
      font-family: 'VT323', 'Courier New', monospace;
      padding: 50px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      font-size: 24px;
      z-index: 99999;
      cursor: pointer;
    `;
    shutdownScreen.innerHTML = `
      <p>It's now safe to turn off your computer.</p>
      <br/>
      <p style="color: #00FF00; animation: blink 1s infinite;">_</p>
      <br/><br/>
      <p style="font-size: 14px; color: #808080;">Click anywhere to restart...</p>
      <style>
        @keyframes blink { 50% { opacity: 0; } }
      </style>
    `;

    document.body.appendChild(shutdownScreen);

    // Add click listener to the shutdown screen only
    shutdownScreen.addEventListener("click", (e) => {
      e.stopPropagation();
      window.location.reload();
    });
  };

  const menuItems = [
    { icon: <FiHome />, label: "Home", path: "/", color: "#00FFFF" },
    { icon: <FiUser />, label: "About Me", path: "/me", color: "#FF1493" },
    {
      icon: <FiBriefcase />,
      label: "Projects",
      path: "/projects",
      color: "#00FF00",
    },
    { icon: <FiMail />, label: "Contact", path: "/contact", color: "#FFFF00" },
  ];

  return (
    <Portal>
      {/* Backdrop - only when menu is open */}
      {isOpen && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={1998}
          onClick={onClose}
        />
      )}

      {/* Start Menu */}
      {isOpen && (
        <Box
          position="fixed"
          bottom="40px"
          left="0"
          zIndex={1999}
          bg="#C0C0C0"
          border="3px outset white"
          boxShadow="4px -4px 0px #404040"
          minW="250px"
          animation="slideUp 0.2s ease-out"
          sx={{
            "@keyframes slideUp": {
              from: { transform: "translateY(100%)", opacity: 0 },
              to: { transform: "translateY(0)", opacity: 1 },
            },
          }}
        >
          {/* Blue sidebar with user info */}
          <Flex>
            <Box
              bg="linear-gradient(180deg, #000080, #0000AA)"
              w="40px"
              py={4}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="flex-end"
            >
              <Text
                sx={{
                  writingMode: "vertical-rl",
                  transform: "rotate(180deg)",
                }}
                fontFamily="'Comic Sans MS', cursive"
                fontWeight="bold"
                fontSize="lg"
                color="white"
                textShadow="1px 1px 0px #000"
                letterSpacing="2px"
              >
                Yosri<span style={{ color: "#FFFF00" }}>95</span>
              </Text>
            </Box>

            {/* Menu Items */}
            <VStack spacing={0} align="stretch" flex={1}>
              {menuItems.map((item) => (
                <Flex
                  key={item.path}
                  align="center"
                  gap={3}
                  px={3}
                  py={2}
                  cursor="pointer"
                  bg={hoveredItem === item.path ? "#000080" : "transparent"}
                  color={hoveredItem === item.path ? "white" : "black"}
                  onMouseEnter={() => setHoveredItem(item.path)}
                  onMouseLeave={() => setHoveredItem(null)}
                  onClick={() => handleNavigate(item.path)}
                  fontFamily="'Comic Sans MS', cursive"
                >
                  <Box
                    color={hoveredItem === item.path ? item.color : "#000080"}
                    fontSize="xl"
                  >
                    {item.icon}
                  </Box>
                  <Text fontWeight="bold">{item.label}</Text>
                </Flex>
              ))}

              {/* Divider */}
              <Box h="2px" bg="#808080" my={1} mx={2} />

              {/* About */}
              <Flex
                align="center"
                gap={3}
                px={3}
                py={2}
                cursor="pointer"
                bg={hoveredItem === "about" ? "#000080" : "transparent"}
                color={hoveredItem === "about" ? "white" : "black"}
                onMouseEnter={() => setHoveredItem("about")}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={() => {
                  setShowAboutDialog(true);
                  onClose();
                }}
                fontFamily="'Comic Sans MS', cursive"
              >
                <Box
                  color={hoveredItem === "about" ? "#FFFF00" : "#000080"}
                  fontSize="xl"
                >
                  <FiHelpCircle />
                </Box>
                <Text fontWeight="bold">About</Text>
              </Flex>

              {/* Documents (Easter Egg) */}
              <Flex
                align="center"
                gap={3}
                px={3}
                py={2}
                cursor="pointer"
                bg={hoveredItem === "docs" ? "#000080" : "transparent"}
                color={hoveredItem === "docs" ? "white" : "black"}
                onMouseEnter={() => setHoveredItem("docs")}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={() => {
                  alert(
                    "📁 My Documents\n\n- resume_final_FINAL_v2.txt\n- budget_1994.xls\n- horny_alien_hentai_tentacles.3gp\n- games/",
                  );
                  onClose();
                }}
                fontFamily="'Comic Sans MS', cursive"
              >
                <Box
                  color={hoveredItem === "docs" ? "#FFFF00" : "#000080"}
                  fontSize="xl"
                >
                  <FiFolder />
                </Box>
                <Text fontWeight="bold">My Documents</Text>
              </Flex>

              {/* Divider */}
              <Box h="2px" bg="#808080" my={1} mx={2} />

              {/* Shut Down */}
              <Flex
                align="center"
                gap={3}
                px={3}
                py={2}
                cursor="pointer"
                bg={hoveredItem === "shutdown" ? "#000080" : "transparent"}
                color={hoveredItem === "shutdown" ? "white" : "black"}
                onMouseEnter={() => setHoveredItem("shutdown")}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={handleShutdown}
                fontFamily="'Comic Sans MS', cursive"
              >
                <Box
                  color={hoveredItem === "shutdown" ? "#FF0000" : "#000080"}
                  fontSize="xl"
                >
                  <FiPower />
                </Box>
                <Text fontWeight="bold">Shut Down...</Text>
              </Flex>
            </VStack>
          </Flex>
        </Box>
      )}

      {/* Shutdown Dialog */}
      {showShutdownDialog && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="rgba(0,0,0,0.5)"
          zIndex={2000}
          display="flex"
          alignItems="center"
          justifyContent="center"
          onClick={() => setShowShutdownDialog(false)}
        >
          <Box
            bg="#C0C0C0"
            border="3px outset white"
            boxShadow="4px 4px 0px #404040"
            minW="350px"
            onClick={(e) => e.stopPropagation()}
          >
            <Flex bg="linear-gradient(90deg, #000080, #1084d0)" px={2} py={1}>
              <Text
                fontFamily="'Comic Sans MS', cursive"
                fontWeight="bold"
                fontSize="sm"
                color="white"
              >
                Shut Down Windows
              </Text>
            </Flex>
            <Box p={4}>
              <Flex align="center" gap={3} mb={4}>
                <Text fontSize="4xl">🖥️</Text>
                <Text fontFamily="'Comic Sans MS', cursive" color="black">
                  What do you want the computer to do?
                </Text>
              </Flex>

              <VStack spacing={2} mb={4} align="stretch">
                <Flex
                  align="center"
                  gap={2}
                  p={2}
                  border="1px solid #808080"
                  cursor="pointer"
                  bg={shutdownOption === "shutdown" ? "#000080" : "transparent"}
                  color={shutdownOption === "shutdown" ? "white" : "black"}
                  onClick={() => setShutdownOption("shutdown")}
                >
                  <input
                    type="radio"
                    checked={shutdownOption === "shutdown"}
                    onChange={() => setShutdownOption("shutdown")}
                  />
                  <Text fontFamily="'Comic Sans MS', cursive">Shut down</Text>
                </Flex>
                <Flex
                  align="center"
                  gap={2}
                  p={2}
                  border="1px solid #808080"
                  cursor="pointer"
                  bg={shutdownOption === "restart" ? "#000080" : "transparent"}
                  color={shutdownOption === "restart" ? "white" : "black"}
                  onClick={() => setShutdownOption("restart")}
                >
                  <input
                    type="radio"
                    checked={shutdownOption === "restart"}
                    onChange={() => setShutdownOption("restart")}
                  />
                  <Text fontFamily="'Comic Sans MS', cursive">Restart</Text>
                </Flex>
              </VStack>

              <Flex justify="center" gap={2}>
                <Box
                  as="button"
                  bg="#C0C0C0"
                  border="2px outset white"
                  px={6}
                  py={1}
                  fontFamily="'Comic Sans MS', cursive"
                  fontWeight="bold"
                  cursor="pointer"
                  _hover={{ bg: "#A0A0A0" }}
                  _active={{ border: "2px inset #808080" }}
                  onClick={confirmShutdown}
                >
                  OK
                </Box>
                <Box
                  as="button"
                  bg="#C0C0C0"
                  border="2px outset white"
                  px={4}
                  py={1}
                  fontFamily="'Comic Sans MS', cursive"
                  fontWeight="bold"
                  cursor="pointer"
                  _hover={{ bg: "#A0A0A0" }}
                  _active={{ border: "2px inset #808080" }}
                  onClick={() => setShowShutdownDialog(false)}
                >
                  Cancel
                </Box>
              </Flex>
            </Box>
          </Box>
        </Box>
      )}

      {/* About Dialog */}
      {showAboutDialog && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="rgba(0,0,0,0.5)"
          zIndex={2000}
          display="flex"
          alignItems="center"
          justifyContent="center"
          onClick={() => setShowAboutDialog(false)}
        >
          <Box
            bg="#C0C0C0"
            border="3px outset white"
            boxShadow="4px 4px 0px #404040"
            minW="400px"
            onClick={(e) => e.stopPropagation()}
          >
            <Flex
              bg="linear-gradient(90deg, #000080, #1084d0)"
              px={2}
              py={1}
              justify="space-between"
            >
              <Text
                fontFamily="'Comic Sans MS', cursive"
                fontWeight="bold"
                fontSize="sm"
                color="white"
              >
                About Yosri95
              </Text>
              <Box
                w="16px"
                h="16px"
                bg="#C0C0C0"
                border="2px outset white"
                textAlign="center"
                lineHeight="12px"
                fontSize="10px"
                cursor="pointer"
                _hover={{ bg: "#FF0000", color: "white" }}
                onClick={() => setShowAboutDialog(false)}
              >
                ✕
              </Box>
            </Flex>
            <Box p={4} textAlign="center">
              <Text fontSize="6xl" mb={2}>
                🖥️
              </Text>
              <Text
                fontFamily="'Comic Sans MS', cursive"
                fontWeight="bold"
                fontSize="xl"
                color="black"
              >
                Yosri95
              </Text>
              <Text
                fontFamily="'Comic Sans MS', cursive"
                fontSize="sm"
                color="black"
                mb={3}
              >
                Version 4.0 (Build 1995)
              </Text>
              <Box
                bg="white"
                border="2px inset #808080"
                p={3}
                mb={3}
                textAlign="left"
              >
                <Text
                  fontFamily="'Comic Sans MS', cursive"
                  fontSize="sm"
                  color="black"
                >
                  Copyright © 2024 Yosri Bedoui
                  <br />
                  All rights reserved.
                  <br />
                  <br />
                  This resume is licensed to:
                  <br />
                  <Text as="span" fontWeight="bold">
                    Awesome Recruiters Everywhere
                  </Text>
                  <br />
                  <br />
                  Physical Memory: ∞ MB
                  <br />
                  Coffee Consumed: Too much ☕
                </Text>
              </Box>
              <Box
                as="button"
                bg="#C0C0C0"
                border="2px outset white"
                px={6}
                py={1}
                fontFamily="'Comic Sans MS', cursive"
                fontWeight="bold"
                cursor="pointer"
                _hover={{ bg: "#A0A0A0" }}
                _active={{ border: "2px inset #808080" }}
                onClick={() => setShowAboutDialog(false)}
              >
                OK
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Portal>
  );
};
